'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { isActionKey, menuBreadcrumbItems } from '@/config/menu-catalog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useMenuRoleSetList, useRemoveMenuRoleSet } from '@/hooks/useMenuRoleSet';
import type { MenuRoleSet } from '@/types/menu-role-set';
import { HTTPError } from 'ky';
import { KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { RoleSetFormDialog } from './role-set-form-dialog';

export function RoleSetList() {
  const { toast } = useToast();
  const { data, loading, refetch } = useMenuRoleSetList();
  const { removeMenuRoleSet } = useRemoveMenuRoleSet();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MenuRoleSet | null>(null);

  const roleSets = data ?? [];

  const handleNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleEdit = (roleSet: MenuRoleSet) => {
    setEditing(roleSet);
    setDialogOpen(true);
  };

  const handleDelete = async (roleSet: MenuRoleSet) => {
    if (!confirm(`'${roleSet.name}' 역할세트를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await removeMenuRoleSet(roleSet.id);
      toast({ title: '삭제 완료', description: '역할세트가 삭제되었습니다' });
      refetch();
    } catch (error) {
      const status = error instanceof HTTPError ? error.response.status : undefined;
      if (status === 409) {
        toast({
          title: '삭제 불가',
          description: '사용 중인 세트는 삭제할 수 없습니다',
          variant: 'destructive',
        });
      } else {
        toast({
          title: '삭제 실패',
          description: '역할세트 삭제 중 오류가 발생했습니다',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('menu-role-sets')} />
      }
      title='역할세트 목록'
    >
      <div className='space-y-4'>
        <div className='flex justify-end'>
          <Button onClick={handleNew}>
            <Plus className='mr-2 h-4 w-4' />새 세트
          </Button>
        </div>

        <div className='border rounded-lg'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>이름</TableHead>
                <TableHead>설명</TableHead>
                <TableHead className='w-24'>메뉴 수</TableHead>
                <TableHead className='w-20'>활성</TableHead>
                <TableHead className='text-right w-28'>작업</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className='h-4 w-24' />
                    </TableCell>
                    <TableCell>
                      <Skeleton className='h-4 w-40' />
                    </TableCell>
                    <TableCell>
                      <Skeleton className='h-4 w-10' />
                    </TableCell>
                    <TableCell>
                      <Skeleton className='h-6 w-12' />
                    </TableCell>
                    <TableCell className='text-right'>
                      <Skeleton className='h-8 w-16 ml-auto' />
                    </TableCell>
                  </TableRow>
                ))
              ) : roleSets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className='text-center py-12'>
                    <div className='flex flex-col items-center gap-2'>
                      <KeyRound className='h-8 w-8 text-muted-foreground' />
                      <div className='text-muted-foreground'>
                        등록된 역할세트가 없습니다
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                roleSets.map(roleSet => (
                  <TableRow
                    key={roleSet.id}
                    className='transition-colors hover:bg-muted/50'
                  >
                    <TableCell className='font-medium'>{roleSet.name}</TableCell>
                    <TableCell className='text-muted-foreground'>
                      {roleSet.description || '-'}
                    </TableCell>
                    <TableCell>
                      {roleSet.menuKeys.filter(key => !isActionKey(key)).length}
                      {roleSet.menuKeys.some(isActionKey) && (
                        <span className='ml-1 text-muted-foreground'>
                          +{roleSet.menuKeys.filter(isActionKey).length}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {roleSet.isActive ? (
                        <Badge variant='outline'>활성</Badge>
                      ) : (
                        <Badge variant='secondary'>비활성</Badge>
                      )}
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='flex justify-end gap-1'>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='h-8 w-8 p-0'
                          onClick={() => handleEdit(roleSet)}
                        >
                          <Pencil className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='h-8 w-8 p-0 text-red-600'
                          onClick={() => handleDelete(roleSet)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <RoleSetFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        roleSet={editing}
        onSaved={refetch}
      />
    </ExpandableCard>
  );
}
